/* English copy. Mirrors the namespaces in ./de.js exactly. */
const en = {
  common: {
    skipToContent: "Skip to content",
    home: "Home",
    languageLabel: "Change language",
    site: {
      tagline: "AI-Powered Source-to-Pay for the Enterprise",
      description:
        "Mercoris is the AI-powered Source-to-Pay platform for enterprise procurement: supplier management, sourcing, contracts, invoices and spend analytics on one low-code foundation.",
      shortPitch: "AI-powered Source-to-Pay for enterprises that need speed without giving up control.",
      offices: {
        unitedStates: "United States",
        germany: "Germany",
        india: "India",
      },
    },
    footer: {
      legal: "All rights reserved.",
      columns: {
        sitemap: "Sitemap",
        sourceToPay: "Source to Pay",
        solutions: "Solutions",
        platform: "Platform",
      },
      viewAllApps: "View all apps →",
    },
  },

  nav: {
    primary: {
      home: "Home",
      s2p: "Source to Pay",
      solutions: "Solutions",
      platform: "Platform",
      about: "About Us",
      contact: "Contact Us",
    },
    viewAll: "View all →",
    mobileOverview: "{{title}} overview",
    menus: {
      s2p: {
        kicker: "Source to Pay",
        title: "Source-to-Pay Suite",
        blurb:
          "One connected flow from sourcing event to settled invoice — every module AI-assisted, audit-ready and live on day one.",
        groups: {
          vendorManagement: {
            heading: "Vendor Management",
            sub: "Transform vendor management with AI-powered apps.",
          },
          other: {
            heading: "Other Source to Pay apps",
            sub: "Apps for contracts, compliance, performance, risk and supplier communication.",
          },
        },
      },
      solutions: {
        kicker: "Solutions",
        title: "Solutions",
        blurb:
          "Outcome-led programmes for procurement, finance and operations teams modernising how work gets done.",
      },
      platform: {
        kicker: "Platform",
        title: "The Mercoris Platform",
        blurb:
          "A low-code foundation your teams extend themselves — builders, data, AI and enterprise-grade controls.",
      },
    },
    /* Display label/blurb for every nav + mega-menu + footer link, keyed by
       the existing stable `fragment` string computed in constants/navigation.js. */
    items: {
      "vendor-onboarding": { label: "Vendor Onboarding", blurb: "Self-serve registration" },
      "purchase-requisition": { label: "Purchase Requisition", blurb: "Guided intake and approvals" },
      "purchase-order": { label: "Purchase Order", blurb: "Automated PO issuance" },
      "e-auctions": { label: "E-Auctions", blurb: "Live competitive bidding" },
      "request-for-quotation": { label: "Request for Quotation", blurb: "Structured sourcing events" },
      "goods-receipt-note": { label: "Goods Receipt Note", blurb: "Receipt and quality capture" },
      invoice: { label: "Invoice", blurb: "Touchless three-way match" },
      "digital-transformation": { label: "Digital Transformation", blurb: "Retire paper and email trails" },
      "workflow-automation": { label: "Workflow Automation", blurb: "Rules, routing and SLAs" },
      "case-management": { label: "Case Management", blurb: "Structured issue resolution" },
      marketplace: { label: "Marketplace", blurb: "Curated catalogue buying" },
      "procurement-automation": { label: "Procurement Automation", blurb: "Requisition to payment" },
      "vendor-management": { label: "Vendor Management", blurb: "Lifecycle in one system" },
      "supply-chain-management": { label: "Supply Chain Management", blurb: "Visibility across tiers" },
      "low-code-app-builder": { label: "Low-Code App Builder", blurb: "Ship internal apps in days" },
      "form-builder": { label: "Form Builder", blurb: "Drag-and-drop data capture" },
      "workflow-builder": { label: "Workflow Builder", blurb: "Visual process design" },
      "dashboard-reports": { label: "Dashboard & Reports", blurb: "Operational reporting" },
      analytics: { label: "Analytics", blurb: "Self-serve exploration" },
      "ai-engine": { label: "AI Engine", blurb: "Agents, extraction, insight" },
      "api-integrations": { label: "API & Integrations", blurb: "ERP and best-of-breed" },
      "role-management": { label: "Role Management", blurb: "Granular permissions" },
      "service-entry-sheet": { label: "Service Entry Sheet" },
      "advance-shipping-notice": { label: "Advance Shipping Notice" },
      "contract-management": { label: "Contract Management" },
      "supplier-certification": { label: "Supplier Certification" },
      "supply-chain-issue-management": { label: "Supply Chain Issue Management" },
      "supplier-rating": { label: "Supplier Rating" },
      "supplier-compliance-management": { label: "Supplier Compliance Management" },
      "supplier-performance-management": { label: "Supplier Performance Management" },
      "supplier-bulletin-management": { label: "Supplier Bulletin Management" },
    },
  },

  home: {
    hero: {
      badge: "✦ New breakthrough AI platform",
      title: "The AI-Powered Low-Code & No-Code Core for Enterprise Innovation",
      lede: "Mercoris Core is the foundational AI engine that powers every app, workflow and integration on our platform. It provides the essential infrastructure for rapid, code-free development, transforming complex business logic into high-performance digital assets.",
      bookDemo: "Book a demo",
      explorePlatform: "Explore Platform",
      mockTitle: "Spend Command Center",
      mockPill: "AI Engine active",
      cycleTimeTitle: "Cycle time by stage",
      aiActionsTitle: "AI actions today",
    },
    heroStats: [
      { label: "Addressable spend", value: "$4.2B", delta: "94% under management" },
      { label: "Requisition → PO", value: "1.4d", delta: "↓ 71% year on year" },
      { label: "Touchless invoices", value: "88%", delta: "↑ 23 pts since AI Engine" },
      { label: "Active suppliers", value: "12,480", delta: "99.1% compliant" },
    ],
    cycleBars: [
      { label: "Intake" },
      { label: "Sourcing" },
      { label: "Award" },
      { label: "PO" },
      { label: "Receipt" },
      { label: "Invoice" },
      { label: "Pay" },
    ],
    aiActions: [
      { title: "Matched 1,204 invoices to PO and receipt", meta: "Exceptions routed to 3 approvers" },
      { title: "Flagged duplicate payment risk — €48,200", meta: "Vendor: Kernwerk GmbH · held" },
      { title: "Drafted RFQ for MRO consumables", meta: "9 qualified suppliers suggested" },
      { title: "Renewal alert: 12 contracts in 30 days", meta: "Est. leakage avoided $310k" },
    ],
    trustedBy: "Trusted by",
    certs: {
      title: "Your business is",
      titleAccent: "safe with us",
      lede: "Mercoris upholds compliance with esteemed certifications, ensuring our commitment to excellence in data protection, quality management and security standards.",
      gdpr: "GDPR",
      gdprLabel: "Compliant",
      tisax: "TISAX",
      tisaxLabel: "Compliant",
      isoStandards: ["ISO 9001", "ISO 27001", "ISO 27034", "ISO 12207", "ISO 22301", "ISO 20000"],
    },
    why: {
      kicker: "Why Mercoris",
      title: "Built for the messy reality of enterprise buying",
      lede: "Most procurement suites force your process into their template. Mercoris adapts to yours — with low-code configuration, AI that reads your documents, and controls your auditors already recognise.",
    },
    whyCards: [
      { title: "Configure, don't customise", body: "Forms, workflows and approvals change in the browser. No consultants, no upgrade debt when the process shifts." },
      { title: "One supplier truth", body: "Qualification, compliance, performance and spend live on a single vendor record shared across every module." },
      { title: "AI you can audit", body: "Every extraction, recommendation and automated action shows its evidence and can be reversed in one click." },
      { title: "Rollout in weeks", body: "Start with one category or one module. Expand across entities without a replatform or a second contract." },
    ],
    ai: {
      kicker: "AI Capabilities",
      title: "An AI engine that does the procurement busywork",
      lede: "Document intelligence, anomaly detection and agentic workflows work inside your process — not beside it. Every AI action is explainable, reversible and logged.",
      link: "See the AI Engine",
    },
    aiCapabilities: [
      { title: "Document intelligence", body: "Invoices, contracts, certificates and quotes parsed into structured fields with confidence scoring." },
      { title: "Autonomous matching", body: "Three-way match, tolerance handling and exception routing without a keystroke on clean transactions." },
      { title: "Risk and anomaly detection", body: "Duplicate payments, price drift, split POs and sanctions exposure surfaced before they settle." },
      { title: "Sourcing copilot", body: "Draft RFQs, suggest qualified suppliers and summarise bid tabulations in your own commercial language." },
      { title: "Conversational analytics", body: "Ask for spend by category, entity or supplier and get a governed answer with the query behind it." },
    ],
    s2p: {
      kicker: "Source to Pay",
      title: "The full Source-to-Pay lifecycle. Built for scale, compliance and control",
      link: "Full Source-to-Pay tour",
      appsPanelTitle: "Purpose-built apps",
      appsPanelRest: "covering the entire procure-to-pay flow",
      buildTitle: "Want to build your own app?",
      buildLine: "Power it with our AI-based business process automation platform.",
      capsPanelTitle: "Key capabilities",
      capsPanelRest: "available across every app",
      supplierPanelTitle: "Supplier-side apps",
      supplierPanelRest: "for the vendor lifecycle",
    },
    coreApps: [
      { label: "Vendor Onboarding" },
      { label: "Purchase Requisition" },
      { label: "Request for Quotation" },
      { label: "Contract Management" },
      { label: "Reverse / Forward Auction" },
      { label: "Purchase Orders" },
      { label: "Advance Shipping Notice" },
      { label: "Invoice" },
    ],
    supplierApps: [
      { label: "Goods Receipt Note" },
      { label: "Service Entry Sheet" },
      { label: "Supplier Bulletin Management" },
      { label: "Supplier Compliance Management" },
      { label: "Supplier Performance Management" },
      { label: "Supply Chain Issue Management" },
      { label: "Supplier Certification" },
      { label: "Supplier Rating" },
    ],
    capabilities: [
      { label: "Integration with multiple ERPs" },
      { label: "Fully configurable, no code" },
      { label: "2 & 3-way match" },
      { label: "Governance & compliance" },
      { label: "OCR + AP automation" },
      { label: "Dashboards & analytics" },
    ],
    buildStats: [
      { value: "10x", label: "Save employee time and effort" },
      { value: "↓ TCO", label: "Low total cost of ownership" },
      { value: "Flexible", label: "Easy to configure and use" },
    ],
    solutions: {
      kicker: "Solutions",
      title: "Solutions built around the outcome you're chasing",
      lede: "Pick the problem — slow approvals, supplier risk, off-contract spend — and Mercoris configures the modules, workflows and reporting that close it.",
      link: "Explore solutions",
    },
    solutionCards: {
      "digital-transformation": { body: "Retire email approvals and shared drives with one governed system of record.", outcome: "60% less manual handling" },
      "workflow-automation": { body: "Rules, routing, escalations and SLAs designed visually and versioned safely.", outcome: "4x faster approvals" },
      "case-management": { body: "Supplier queries, disputes and exceptions handled with owners and deadlines.", outcome: "Resolution in hours" },
      marketplace: { body: "Curated catalogues and punchout so buyers self-serve inside policy.", outcome: "92% on-contract buying" },
      "procurement-automation": { body: "Requisition to payment with AI handling the repetitive middle.", outcome: "$0.94 per invoice" },
      "vendor-management": { body: "One lifecycle from qualification to performance review and offboarding.", outcome: "99% compliance rate" },
      "supply-chain-management": { body: "Visibility across suppliers, tiers and commitments before disruption bites.", outcome: "Risk seen 30 days out" },
    },
    platform: {
      kicker: "Platform",
      title: "Low-code underneath. Enterprise-grade all the way through",
      lede: "Mercoris ships as a working S2P suite and stays configurable forever. Your team builds forms, workflows, dashboards and apps without waiting on a release cycle.",
      link: "Tour the platform",
    },
    features: {
      kicker: "Key features",
      title: "The details that decide the rollout",
    },
    featureCards: [
      { title: "Multi-entity, multi-currency", body: "Legal entities, tax rules, currencies and languages handled in one tenant." },
      { title: "Policy-aware approvals", body: "Delegation of authority, thresholds and segregation of duties enforced automatically." },
      { title: "Supplier portal", body: "Suppliers register, submit documents, bid and track payments themselves." },
      { title: "Contract intelligence", body: "Clause extraction, obligation tracking and renewal alerts across the repository." },
      { title: "Budget checks", body: "Real-time commitment and budget validation before a requisition is approved." },
      { title: "Mobile approvals", body: "Approve, reject or ask a question from a phone with the full audit context." },
      { title: "Configurable scorecards", body: "Weighted supplier KPIs with automated data capture and review cadence." },
      { title: "Immutable audit trail", body: "Every field change, approval and AI action timestamped and exportable." },
    ],
    outcomeStats: [
      { to: 71, suffix: "%", label: "Faster requisition-to-PO", note: "Median across enterprise rollouts in year one." },
      { to: 88, suffix: "%", label: "Touchless invoice rate", note: "Straight-through processing with the AI Engine on." },
      { to: 4.2, prefix: "$", suffix: "B", label: "Spend under management", note: "Across manufacturing, energy, pharma and public sector." },
      { to: 6, suffix: " wks", label: "Typical time to first go-live", note: "One category live before the full programme lands." },
    ],
    about: {
      kicker: "About Us",
      title: "Building Enterprise Procurement Platforms for Complex Businesses",
      lede: "Mercoris builds and ships a single enterprise Source-to-Pay platform for organisations where every system carries real operational and regulatory weight — backed by senior teams and 24/7 support across the US, Germany and India.",
      link: "More about us",
    },
    aboutStats: [
      { value: "3", label: "Delivery regions — US, Germany, India" },
      { value: "24/7", label: "Technologists and alliance experts" },
      { value: "16", label: "Source-to-Pay apps in the suite" },
      { value: "200+", label: "Enterprises served to date" },
    ],
    cta: {
      title: "See Mercoris on your own process",
      lede: "A 30-minute working session with a procurement architect — we map one of your live categories end to end and show exactly what automation changes.",
      bookDemo: "Book a demo",
      talkToSales: "Talk to sales",
    },
    contact: {
      kicker: "Contact Us",
      title: "Let's Transform Your",
      titleLine2: "Procurement Strategy",
      lede: "Want to see how Mercoris could fit your business needs, or explore a quick demo? Talk to us.",
      link: "Open the contact form",
      emailLabel: "Email",
      emailNote: "Demos, pricing and pilots — reply within one business day",
    },
  },

  about: {
    hero: {
      title: "We build procurement software",
      titleLine2: "for people who have lived the problem",
      lede: "Mercoris was founded by procurement operators and platform engineers who spent years watching good teams held back by software that could not bend. So we built one that does.",
      breadcrumb: "About Us",
    },
    facts: [
      { value: "2019", label: "Founded, headquartered in Leander, TX, USA" },
      { value: "$4.2B", label: "Enterprise spend managed on the platform" },
      { value: "5", label: "Data residency regions in production" },
      { value: "12,480", label: "Suppliers transacting through Mercoris" },
    ],
    company: {
      kicker: "Company overview",
      title: "Building Enterprise Procurement Platforms for Complex Businesses",
    },
    overviewStats: [
      { value: "3", label: "Delivery regions — US, Germany, India" },
      { value: "24/7", label: "Technologists and alliance experts" },
      { value: "16", label: "Source-to-Pay apps in the suite" },
      { value: "200+", label: "Enterprises served to date" },
    ],
    overviewTags: [
      "Product engineering",
      "Platform implementation",
      "ERP integration",
      "Application management",
      "AI enablement",
    ],
    overviewParagraphs: [
      "Mercoris builds enterprise procurement products for organisations across manufacturing, energy, pharmaceuticals, logistics and the public sector — environments where every system carries real operational and regulatory weight.",
      "Our product line centres on a single platform: a complete Source-to-Pay suite on a low-code foundation, with an AI engine built for settings where every automated decision must be explainable. Around it we provide implementation, integration and application management to get every customer live on it and keep them there.",
      "We work in small, senior teams supported by technologists and partner alliance experts available 24/7. Every customer gets a named architect who understands their landscape, their ERP and their audit obligations — not a ticket queue.",
    ],
    mission: {
      kicker: "Our Mission",
      body: "Our core purpose at Mercoris is to empower businesses with cutting-edge technology solutions that solve complex business problems. We are passionate about innovation and harness it to facilitate business growth and success.",
      points: [
        "Driving sustainable digital growth",
        "Innovating for a smarter future",
        "Customer-centric IT excellence",
        "Building connected, resilient ecosystems",
      ],
    },
    vision: {
      kicker: "Our Vision",
      body: "Our value proposition lies in our commitment to delivering quality first. We build a world-class enterprise product, backed by a team of technologists and partner alliance experts available 24/7. We pride ourselves on being a trusted product partner for companies of all sizes and across all industries.",
      points: [
        "Commitment to excellence",
        "Empowering global partnerships",
        "Putting customers at the core",
      ],
    },
    valuesTitle: "Core values",
    values: [
      { title: "Operators first", body: "Product decisions are made with people who have run a category, not only with people who have run a roadmap." },
      { title: "Configuration over customisation", body: "If a customer needs code to change, we treat it as a product gap — not a services opportunity." },
      { title: "Explainable by default", body: "Any automated decision must be able to show its evidence. If it cannot, it does not ship." },
      { title: "Security is not a tier", body: "Every control we build is available on every plan, in every region we operate." },
      { title: "Honest about fit", body: "We tell prospects when a requirement is a poor match for us. It costs deals and saves programmes." },
      { title: "Measured in outcomes", body: "Success is cycle time, compliance and realised savings — not licences deployed." },
      { title: "Partners, not vendors", body: "We stay on the account after go-live, because the second year is where the value actually compounds." },
      { title: "Always on, everywhere", body: "Technologists and alliance experts available 24/7 across our US, Germany and India teams." },
    ],
    why: {
      kicker: "Why Mercoris",
      title: "What teams tell us made the difference",
    },
    whyCards: [
      { title: "The process bends to us, not the reverse", body: "Approval matrices, entity rules and category logic configured in weeks, changed in hours." },
      { title: "AI that survives audit", body: "Every extraction and automated match keeps its evidence, confidence score and reversal path." },
      { title: "One platform, not seven modules stitched together", body: "Shared data model, shared permissions, shared reporting — no integration tax between our own products." },
      { title: "Senior people on the account", body: "A named architect who knows your ERP landscape and joins the hard conversations." },
    ],
    enterprise: {
      kicker: "Enterprise focus",
      title: "We optimise for the hard deployments",
      lede: "Multiple entities, several ERPs, unionised sites, regulated categories and an audit committee that reads the detail. That is the environment Mercoris is designed for.",
    },
    enterpriseFocus: [
      { title: "Multi-entity, multi-ERP landscapes", body: "Consolidated reporting across entities that will never share one ERP instance." },
      { title: "Regulated categories", body: "Pharma, energy and public-sector procurement rules supported without bespoke code." },
      { title: "Global supplier bases", body: "Localised portals, tax validation and document requirements per market." },
      { title: "Serious audit obligations", body: "Immutable trails, segregation of duties and evidence packs on demand." },
      { title: "Phased rollouts", body: "Category-by-category deployment with global governance in place from day one." },
    ],
    testimonialsSection: {
      kicker: "Testimonials",
      title: "What our customers say",
      lede: "Discover how our clients have transformed their businesses with our innovative IT solutions and dedicated support.",
    },
    testimonials: [
      {
        quote: "We are delighted to use the workflow application developed by the Mercoris team. It has streamlined our operations and allowed us to adapt processes that suit us best. The team's professionalism and support were exceptional. We highly recommend Mercoris for their top-notch software solutions and seamless delivery.",
        role: "Director, EpicsAI",
      },
      {
        quote: "We are extremely pleased with Mercoris partnership in IT and process consulting for the automotive and manufacturing industry. Their commitment to timely delivery, innovation, and quality work has been remarkable. Mercoris expertise and collaboration have added immense value to our operations and success.",
        role: "MEC Industries",
      },
      {
        quote: "We are thrilled with Mercoris and their transformative IT solutions. After integration, our efficiency improved drastically and manual tasks reduced significantly. The team's dedication, support, and tailored approach were outstanding. We highly recommend Mercoris to any business seeking reliable digital transformation.",
        role: "Director, Koncepthouse",
      },
    ],
    certificationsSection: {
      kicker: "Certifications",
      title: "Independently assessed, annually",
    },
    certifications: [
      { name: "ISO 27001", note: "Information security management system, certified and surveilled annually." },
      { name: "SOC 2 Type II", note: "Independent audit of security, availability and confidentiality controls." },
      { name: "ISO 9001", note: "Quality management across product development and delivery." },
      { name: "GDPR", note: "EU processing, DPAs and regional residency options." },
      { name: "WCAG 2.1 AA", note: "Accessibility conformance across the application and supplier portal." },
    ],
    cta: {
      title: "Come and pressure-test us",
      body: "Bring your hardest requirement — the multi-entity tax rule, the legacy ERP, the auditor's favourite question. We would rather answer it now than in month six.",
      primaryLabel: "Contact Mercoris",
    },
  },

  platform: {
    hero: {
      breadcrumb: "Platform",
      title: "The platform underneath the suite",
      lede: "Mercoris is a low-code application platform that happens to ship with a complete Source-to-Pay suite. Everything you see is configurable — and everything your team builds inherits the same security, audit and AI foundation.",
      demoButton: "Book a technical demo",
      architectureButton: "See the architecture",
    },
    blocks: {
      "low-code-app-builder": {
        kicker: "Low-Code App Builder",
        title: "Build the app the process actually needs",
        body: "Compose data models, screens, logic and permissions visually. Ship an internal application in days, governed by the same controls as the core suite.",
        bullets: ["Visual data modelling", "Screen and layout designer", "Reusable component library", "Environment promotion with change records"],
        panelTitle: "App designer",
        panelMeta: "draft v4",
        rows: [
          { tag: "Entity", label: "Supplier claim", value: "12 fields" },
          { tag: "Screen", label: "Claim intake", value: "responsive" },
          { tag: "Logic", label: "Auto-assign by region", value: "active" },
          { tag: "Access", label: "Buyer, Finance, Admin", value: "3 roles" },
        ],
      },
      "form-builder": {
        kicker: "Form Builder",
        title: "Drag-and-drop capture with real validation",
        body: "Build forms that enforce data quality at entry — conditional fields, lookups against master data, file capture and multi-language labels.",
        bullets: ["Conditional field logic", "Master-data lookups", "File and signature capture", "Localisation per market"],
        panelTitle: "Form: Vendor request",
        panelMeta: "published",
        rows: [
          { tag: "Field", label: "Tax identifier", value: "validated" },
          { tag: "Field", label: "Bank details", value: "encrypted" },
          { tag: "Rule", label: "Show if country = DE", value: "active" },
          { tag: "Output", label: "Creates vendor draft", value: "mapped" },
        ],
      },
      "workflow-builder": {
        kicker: "Workflow Builder",
        title: "Process design your business analysts can own",
        body: "States, transitions, parallel branches, timers and escalations on a visual canvas — with a sandbox to test before anything hits production.",
        bullets: ["Parallel and conditional branches", "SLA timers and escalation paths", "Delegation and substitution rules", "Versioning with safe rollback"],
        panelTitle: "Workflow: PR approval",
        panelMeta: "v7 · live",
        rows: [
          { tag: "Step 1", label: "Budget validation", value: "auto" },
          { tag: "Step 2", label: "Line manager", value: "8h SLA" },
          { tag: "Step 3", label: "Finance > €10k", value: "conditional" },
          { tag: "Step 4", label: "PO creation", value: "auto" },
        ],
      },
      "dashboard-reports": {
        kicker: "Dashboard & Reports",
        title: "Operational answers without a ticket",
        body: "Role-based dashboards for buyers, approvers, AP and executives, plus scheduled reports delivered in the format each audience expects.",
        bullets: ["Role-based dashboard templates", "Drill-through to transactions", "Scheduled email and file delivery", "Export to Excel, CSV and PDF"],
        panelTitle: "Dashboard: AP operations",
        panelMeta: "live",
        rows: [
          { tag: "Widget", label: "Invoices in exception", value: "178" },
          { tag: "Widget", label: "Ageing over 30 days", value: "24" },
          { tag: "Widget", label: "Discounts at risk", value: "€8,400" },
          { tag: "Schedule", label: "Daily 07:00 CET", value: "on" },
        ],
      },
      analytics: {
        kicker: "Analytics",
        title: "Ask the spend question, get a governed answer",
        body: "A semantic layer over procurement data means everyone calculates savings the same way — whether they query in the UI, in natural language or in BI.",
        bullets: ["Governed semantic layer", "Natural-language querying", "Cohort and trend analysis", "Warehouse sync to Snowflake or BigQuery"],
        panelTitle: "Explore: spend",
        panelMeta: "FY26",
        rows: [
          { tag: "Dimension", label: "Category / entity", value: "grouped" },
          { tag: "Measure", label: "Realised savings", value: "$68.4M" },
          { tag: "Filter", label: "Contracted suppliers", value: "on" },
          { tag: "Export", label: "Snowflake share", value: "hourly" },
        ],
      },
      "ai-engine": {
        kicker: "AI Engine",
        title: "Models with guardrails, not gimmicks",
        body: "Extraction, classification, matching, summarisation and agentic actions — each with confidence thresholds, human review paths and a full decision log.",
        bullets: ["Confidence thresholds per process", "Human-in-the-loop review queues", "Full explainability and audit log", "No customer data used for model training"],
        panelTitle: "AI Engine",
        panelMeta: "governed",
        rows: [
          { tag: "Model", label: "Invoice extraction", value: "99.2% F1" },
          { tag: "Model", label: "Spend classification", value: "94% acc." },
          { tag: "Guardrail", label: "Human review < 0.85", value: "enforced" },
          { tag: "Log", label: "Every action recorded", value: "immutable" },
        ],
      },
      "api-integrations": {
        kicker: "API & Integrations",
        title: "Open by default",
        body: "REST APIs, event webhooks, file-based interfaces and certified ERP connectors — with sandbox keys, rate limits and versioning your engineers expect.",
        bullets: ["REST API with OpenAPI spec", "Event webhooks and message queues", "Certified ERP connectors", "SFTP, EDI and cXML interfaces"],
        panelTitle: "API console",
        panelMeta: "v2",
        rows: [
          { tag: "GET", label: "/v2/suppliers", value: "200 OK" },
          { tag: "POST", label: "/v2/requisitions", value: "201" },
          { tag: "Hook", label: "invoice.matched", value: "subscribed" },
          { tag: "Limit", label: "1,000 req / min", value: "tenant" },
        ],
      },
      "role-management": {
        kicker: "Role Management",
        title: "Permissions that mirror your org chart",
        body: "Roles, groups, entity scoping and field-level rules — provisioned automatically from your identity provider and reviewable in one screen.",
        bullets: ["Role and group inheritance", "Entity and category scoping", "Field-level read and write rules", "SCIM provisioning and access reviews"],
        panelTitle: "Access model",
        panelMeta: "audit ready",
        rows: [
          { tag: "Role", label: "Category buyer", value: "412 users" },
          { tag: "Scope", label: "EMEA entities", value: "9 entities" },
          { tag: "Rule", label: "Bank fields hidden", value: "enforced" },
          { tag: "Review", label: "Quarterly attestation", value: "due 12d" },
        ],
      },
    },
    cta: {
      title: "Bring your architects",
      body: "We will walk your security, integration and data teams through the platform in detail — including the parts other vendors skip.",
      primaryLabel: "Book a technical demo",
    },
  },

  solutions: {
    hero: {
      breadcrumb: "Solutions",
      title: "Solutions built around the outcome, not the module",
      lede: "Seven programmes that start with a problem your team already recognises and end with a number your finance function can verify.",
    },
    items: {
      "digital-transformation": {
        title: "Digital Transformation",
        summary: "Move procurement off email, spreadsheets and shared drives onto one governed system of record — without a two-year programme.",
        problem: "Approvals live in inboxes, the real supplier list is a spreadsheet, and nobody can answer a simple question about committed spend without three people digging.",
        approach: "We digitise the highest-friction process first, prove the control improvement, then extend the same patterns across categories and entities.",
        steps: [
          { title: "Map the current state", body: "Two weeks of process discovery with the people who actually do the work." },
          { title: "Configure, don't code", body: "Forms, workflows and approvals built in the browser against your policy." },
          { title: "Migrate and run parallel", body: "Supplier master, open orders and contracts loaded with validation reports." },
          { title: "Scale by entity", body: "Reuse the template, adapt local rules, keep global reporting intact." },
        ],
        features: ["Central system of record", "Digital document repository", "Configurable approval matrix", "Change management toolkit", "Legacy data migration"],
        benefits: ["Single source of truth", "Audit evidence on demand", "Less shadow process", "Faster onboarding of new staff"],
        outcomes: [{ value: "60%", label: "Less manual handling" }, { value: "6 wks", label: "To first go-live" }, { value: "100%", label: "Transactions traceable" }],
        ctaLabel: "Plan a transformation",
        relatedLabel: "See the low-code platform",
      },
      "workflow-automation": {
        title: "Workflow Automation",
        summary: "Design approval and exception flows visually, version them safely, and let the system enforce the SLA instead of a reminder email.",
        problem: "Every exception becomes a chase. Approvers sit on requests, escalations depend on someone noticing, and nobody can prove where the delay happened.",
        approach: "Model the process once in the visual builder, attach rules and SLAs, and give every participant a queue with clear ownership and deadlines.",
        steps: [
          { title: "Model the flow", body: "Drag states, conditions, parallel branches and delegation into place." },
          { title: "Attach the rules", body: "Thresholds, segregation of duties, tolerances and escalation timers." },
          { title: "Test in sandbox", body: "Simulate real transactions before anything reaches production." },
          { title: "Measure and tune", body: "Bottleneck analytics show which step to fix next." },
        ],
        features: ["Visual workflow designer", "Conditional and parallel routing", "SLA timers and escalations", "Delegation and out-of-office", "Version control with rollback"],
        benefits: ["Predictable cycle times", "Fewer escalation emails", "Policy enforced automatically", "Clear accountability per step"],
        outcomes: [{ value: "4x", label: "Faster approvals" }, { value: "92%", label: "Within SLA" }, { value: "0", label: "Manual chase lists" }],
        ctaLabel: "See it on your process",
        relatedLabel: "Workflow builder",
      },
      "case-management": {
        title: "Case Management",
        summary: "Turn supplier queries, invoice disputes and quality issues into tracked cases with owners, deadlines and a resolution history.",
        problem: "Disputes bounce between AP, buyers and the supplier with no ticket, no owner and no record — until the relationship or the payment run suffers.",
        approach: "Structured case types capture the issue, route to the right desk, pull in the transaction context, and close with a documented outcome.",
        steps: [
          { title: "Capture", body: "Cases raised from the portal, an invoice, a receipt or an email." },
          { title: "Route", body: "Assignment by type, category, entity and workload." },
          { title: "Resolve", body: "Full transaction context and messaging in one thread." },
          { title: "Learn", body: "Root-cause tagging feeds supplier scorecards and process fixes." },
        ],
        features: ["Configurable case types", "Supplier-facing messaging", "SLA clocks and priorities", "Root cause taxonomy", "Resolution knowledge base"],
        benefits: ["Nothing falls through", "Suppliers stop calling buyers", "Repeat issues get fixed", "Evidence for negotiations"],
        outcomes: [{ value: "8h", label: "Median resolution" }, { value: "-46%", label: "Repeat disputes" }, { value: "100%", label: "Cases auditable" }],
        ctaLabel: "Discuss case handling",
        relatedLabel: "Invoice management",
      },
      marketplace: {
        title: "Marketplace",
        summary: "Give buyers a curated internal catalogue with contracted pricing, punchout to supplier sites, and policy applied before checkout.",
        problem: "Buyers go off-contract because finding the approved item is harder than raising a free-text request — and savings leak one order at a time.",
        approach: "A shopping experience your staff recognise, wired to contracted catalogues, punchout partners and the approval rules behind the scenes.",
        steps: [
          { title: "Load catalogues", body: "Contracted items, price files and punchout connections onboarded." },
          { title: "Apply policy", body: "Preferred suppliers, spend limits and category rules per entity." },
          { title: "Enable self-service", body: "Search, compare, add to cart and submit in under a minute." },
          { title: "Watch the leakage close", body: "Off-catalogue requests flagged with the contracted alternative." },
        ],
        features: ["Internal catalogue management", "cXML punchout", "Contracted price enforcement", "Preferred supplier steering", "Guided free-text intake"],
        benefits: ["Higher on-contract spend", "Fewer maverick purchases", "Happier requesters", "Cleaner spend data"],
        outcomes: [{ value: "92%", label: "On-contract buying" }, { value: "-38%", label: "Free-text requests" }, { value: "< 1 min", label: "To raise an order" }],
        ctaLabel: "See a catalogue demo",
        relatedLabel: "Purchase requisition",
      },
      "procurement-automation": {
        title: "Procurement Automation",
        summary: "Automate the repetitive middle of procure-to-pay so the team spends its time on category strategy and supplier relationships.",
        problem: "Skilled procurement people spend their week re-keying data, matching documents and answering status questions that a system should answer.",
        approach: "AI handles extraction, matching and routine communication; humans handle judgement calls. Confidence thresholds keep you in control of the boundary.",
        steps: [
          { title: "Baseline the effort", body: "Where the touches are, and what each one costs today." },
          { title: "Automate the clean path", body: "Straight-through processing for transactions inside tolerance." },
          { title: "Design the exceptions", body: "Clear queues, owners and deadlines for everything else." },
          { title: "Raise the threshold", body: "Expand automation as confidence data proves itself." },
        ],
        features: ["AI document extraction", "Automated three-way match", "Auto-PO from approved requisitions", "Supplier status self-service", "Exception queue management"],
        benefits: ["Lower cost per transaction", "Faster supplier payment", "Discounts captured", "Team redeployed to strategy"],
        outcomes: [{ value: "88%", label: "Touchless invoices" }, { value: "$0.94", label: "Cost per invoice" }, { value: "+23 pts", label: "Discount capture" }],
        ctaLabel: "Model your savings",
        relatedLabel: "Invoice management",
      },
      "vendor-management": {
        title: "Vendor Management",
        summary: "Run the full supplier lifecycle — qualification, onboarding, compliance, performance and offboarding — from one record.",
        problem: "Supplier data lives in four systems, compliance evidence sits in a folder someone owns, and performance conversations rely on anecdotes.",
        approach: "One governed supplier profile feeds every module, with automated monitoring and scorecards built from actual transaction data.",
        steps: [
          { title: "Consolidate", body: "Merge duplicate records and reconcile against ERP masters." },
          { title: "Qualify", body: "Risk-based questionnaires and document validation by category." },
          { title: "Monitor", body: "Continuous compliance, sanctions and financial health checks." },
          { title: "Develop", body: "Scorecards, improvement plans and structured business reviews." },
        ],
        features: ["Golden supplier record", "Self-service supplier portal", "Automated compliance monitoring", "Weighted performance scorecards", "Offboarding workflow"],
        benefits: ["Trusted supplier data", "Lower third-party risk", "Objective performance reviews", "Faster supplier setup"],
        outcomes: [{ value: "99%", label: "Compliance rate" }, { value: "3 days", label: "Invite to active" }, { value: "0.3%", label: "Duplicate records" }],
        ctaLabel: "Review your supplier base",
        relatedLabel: "Vendor onboarding module",
      },
      "supply-chain-management": {
        title: "Supply Chain Management",
        summary: "See commitments, dependencies and risk across suppliers and tiers early enough to do something about them.",
        problem: "Disruption arrives as a phone call. Single-source dependencies and concentration risk only become visible once a line has already stopped.",
        approach: "Combine order commitments, supplier risk signals and category concentration into one view, with alerts that fire on leading indicators.",
        steps: [
          { title: "Map dependencies", body: "Which suppliers, sites and categories carry critical volume." },
          { title: "Instrument signals", body: "Delivery performance, financial health, geography and news events." },
          { title: "Set thresholds", body: "Alerts on concentration, lead-time drift and confirmation gaps." },
          { title: "Plan alternatives", body: "Qualified backup suppliers ready before you need them." },
        ],
        features: ["Order commitment visibility", "Supplier risk scoring", "Concentration analysis", "Lead-time drift alerts", "Backup supplier register"],
        benefits: ["Earlier warning", "Fewer expedite costs", "Stronger negotiating position", "Board-ready risk reporting"],
        outcomes: [{ value: "30 days", label: "Typical early warning" }, { value: "-27%", label: "Expedite spend" }, { value: "1 view", label: "Across every tier" }],
        ctaLabel: "Assess your exposure",
        relatedLabel: "Supplier performance",
      },
    },
    solutionKicker: "Solution",
    problemLabel: "The problem",
    approachLabel: "The Mercoris approach",
    featuresLabel: "Features",
    benefitsLabel: "Benefits",
    cta: {
      title: "Not sure which programme fits?",
      body: "Send us the process that frustrates your team most. We will map it against these solutions and tell you honestly where the return is — and where it is not.",
      primaryLabel: "Talk to a specialist",
    },
  },

  sourceToPay: {
    hero: {
      breadcrumb: "Source to Pay",
      trustChip1Prefix: "Trusted by",
      trustChip1Strong: "200+",
      trustChip1Suffix: "enterprises",
      trustChip2Strong: "Live in weeks",
      trustChip2Suffix: ", not months",
      titlePrefix: "Cut sourcing cycles by up to 80% with our",
      titleAccent: "AI-based Source-to-Pay platform",
      lede: "A unified, enterprise-ready platform across the entire sourcing-to-payment lifecycle that delivers:",
      walkthroughButton: "Book a walkthrough",
      jumpToModules: "Jump to modules",
    },
    heroPoints: [
      { label: "End-to-end supplier collaboration" },
      { label: "Real-time analytics" },
      { label: "Budget control" },
      { label: "Supplier compliance management" },
      { label: "Integrates with SAP, Oracle, Dynamics, Workday and other ERPs" },
    ],
    lifecycle: {
      kicker: "The flow",
      title: "Six stages, no handoff gaps",
      lede: "Each stage inherits the data, documents and approvals from the one before it. The audit trail is built as you work, not assembled afterwards.",
    },
    stages: [
      { name: "Source", body: "Intake, RFQ events, reverse auctions and structured award decisions.", metric: "38% faster events" },
      { name: "Qualify", body: "Screening, scoring, onboarding and continuous compliance monitoring.", metric: "9 days to qualify" },
      { name: "Contract", body: "Guided authoring, clause libraries, e-signature and obligations.", metric: "11 days to signature" },
      { name: "Buy", body: "Requisitions, budget checks, approvals and automated PO dispatch.", metric: "1.4 days to PO" },
      { name: "Receive", body: "Mobile goods receipt, quality outcomes and instant match triggers.", metric: "98% same-day" },
      { name: "Pay", body: "AI matching, exception handling, settlement and spend analysis.", metric: "88% touchless" },
    ],
    ai: {
      kicker: "AI-powered procurement",
      title: "The engine works the queue while your team works the strategy",
      lede: "Mercoris AI reads the documents, applies your tolerances, and escalates only what genuinely needs a human. Confidence thresholds are yours to set, per category and per entity.",
      queueTitle: "Exception queue",
      queueMeta: "auto-triaged",
    },
    aiTags: ["Document extraction", "Three-way matching", "Duplicate detection", "Supplier recommendation", "Bid summarisation", "Spend classification", "Renewal alerts", "Anomaly scoring"],
    exceptionQueue: [
      { text: "Price variance €1,240 above contract rate", badge: "Review", warm: true },
      { text: "Quantity mismatch against goods receipt", badge: "Open", warm: true },
      { text: "Possible duplicate of INV-4390", badge: "Alert", warm: true },
      { text: "Within tolerance — posted automatically", badge: "Done", warm: false },
      { text: "Missing PO reference, supplier notified", badge: "Chase", warm: true },
    ],
    benefitsTitle: "What changes in the first two quarters",
    benefits: [
      { title: "Cost per transaction falls", body: "Manual touches disappear from invoices, POs and supplier admin — the AP team stops chasing and starts reviewing exceptions." },
      { title: "Savings actually land", body: "Negotiated rates are enforced at requisition, so contracted pricing shows up in the ledger instead of the business case." },
      { title: "Risk stops surprising you", body: "Expiring certificates, sanctions hits and single-source dependencies surface weeks before they become incidents." },
      { title: "Audits get boring", body: "Every approval, document and AI decision is timestamped and exportable, so evidence requests take minutes." },
    ],
    modules: {
      "vendor-onboarding": {
        kicker: "Suppliers onboard themselves, correctly.",
        name: "Vendor Onboarding",
        body: "A guided portal collects tax, banking, compliance and diversity data with validation at the point of entry — so nothing bounces back to procurement, and the vendor record lands in ERP clean.",
        bullets: ["Branded self-service portal", "Country-specific tax validation", "Duplicate detection on creation", "Straight-to-ERP vendor creation"],
        panelTitle: "Onboarding tracker", panelMeta: "this week",
        rows: [
          { label: "Invitation sent", value: "42 suppliers", pill: "Sent", warm: false },
          { label: "Details submitted", value: "36 suppliers", pill: "Active", warm: false },
          { label: "Compliance check", value: "31 suppliers", pill: "Auto", warm: false },
          { label: "ERP created", value: "29 suppliers", pill: "Done", warm: false },
        ],
        statA: "72%", statALabel: "Fewer back-and-forths", statB: "3 days", statBLabel: "Invite to active",
      },
      "purchase-requisition": {
        kicker: "Intake that guides buyers to the right path.",
        name: "Purchase Requisition",
        body: "One request form routes to catalogue, contract or sourcing automatically, checks budget in real time, and collects the approvals policy requires.",
        bullets: ["Guided intake with category logic", "Real-time budget and commitment check", "Delegation of authority enforcement", "Mobile approvals with full context"],
        panelTitle: "Requisition REQ-88213", panelMeta: "€24,500",
        rows: [
          { label: "Budget check", value: "Cost centre 4410", pill: "Pass", warm: false },
          { label: "Contract match", value: "MSA-2024-17", pill: "Found", warm: false },
          { label: "Approver 1", value: "Line manager", pill: "Approved", warm: false },
          { label: "Approver 2", value: "Finance", pill: "Pending", warm: true },
        ],
        statA: "1.4 days", statALabel: "Requisition to PO", statB: "96%", statBLabel: "First-time-right",
      },
      "purchase-order": {
        kicker: "Orders issued the moment approval lands.",
        name: "Purchase Order",
        body: "POs generate from approved requisitions, contracts or catalogue carts, dispatch to suppliers in their preferred format, and track acknowledgement.",
        bullets: ["Automatic PO creation and dispatch", "cXML, EDI, email and portal delivery", "Change orders with version history", "Acknowledgement and ship-date tracking"],
        panelTitle: "Open orders", panelMeta: "1,846",
        rows: [
          { label: "Acknowledged", value: "1,612 orders", pill: "82%", warm: false },
          { label: "Awaiting response", value: "168 orders", pill: "Chase", warm: true },
          { label: "Change requested", value: "44 orders", pill: "Review", warm: true },
          { label: "Overdue delivery", value: "22 orders", pill: "Alert", warm: true },
        ],
        statA: "< 2 min", statALabel: "Approval to dispatch", statB: "82%", statBLabel: "Auto-acknowledged",
      },
      "e-auctions": {
        kicker: "Let the market find the price.",
        name: "E-Auctions",
        body: "Run reverse and forward auctions — English, Dutch, Japanese and rank-based — with live leaderboards, anti-sniping extensions and full bid history for audit.",
        bullets: ["Reverse and forward auction formats", "Live rank and price feedback", "Automatic time extensions", "Complete bid audit log"],
        panelTitle: "Auction AUC-114", panelMeta: "live · 04:12",
        rows: [
          { label: "Bidder A", value: "€412,000", pill: "Rank 1", warm: false },
          { label: "Bidder C", value: "€418,500", pill: "Rank 2", warm: false },
          { label: "Bidder B", value: "€421,000", pill: "Rank 3", warm: false },
          { label: "Savings vs baseline", value: "€63,400", pill: "13.3%", warm: false },
        ],
        statA: "13.3%", statALabel: "Typical event savings", statB: "42 min", statBLabel: "Median duration",
      },
      "request-for-quotation": {
        kicker: "Structured sourcing events without the spreadsheet.",
        name: "Request for Quotation",
        body: "Build events from templates, invite qualified suppliers, collect sealed responses and compare like for like on a normalised bid tabulation.",
        bullets: ["Multi-round, multi-lot events", "Sealed bids with timed release", "Automatic bid normalisation", "AI-drafted scopes and questions"],
        panelTitle: "Event RFQ-2291", panelMeta: "8 bidders",
        rows: [
          { label: "Lot 1 — Castings", value: "6 responses", pill: "Open", warm: true },
          { label: "Lot 2 — Machining", value: "8 responses", pill: "Open", warm: true },
          { label: "Lot 3 — Coating", value: "5 responses", pill: "Closed", warm: false },
          { label: "Award recommendation", value: "AI draft ready", pill: "New", warm: true },
        ],
        statA: "38%", statALabel: "Faster event cycle", statB: "6.4%", statBLabel: "Average savings",
      },
      "service-entry-sheet": {
        kicker: "Proof of work, signed off where it happens.",
        name: "Service Entry Sheet",
        body: "Contractors log effort against the service PO, site owners confirm it on mobile, and the approved entry sheet releases the invoice — no paper timesheets in between.",
        bullets: ["Effort capture against service POs", "Rate card and milestone validation", "Site sign-off from mobile", "Automatic release to invoicing"],
        panelTitle: "Service entries", panelMeta: "week 30",
        rows: [
          { label: "Submitted", value: "148 entries", pill: "Open", warm: true },
          { label: "Site approved", value: "121 entries", pill: "Done", warm: false },
          { label: "Rate variance", value: "9 entries", pill: "Review", warm: true },
          { label: "Released to AP", value: "121 entries", pill: "Auto", warm: false },
        ],
        statA: "82%", statALabel: "Approved within 24h", statB: "4.1%", statBLabel: "Rate variance caught",
      },
      "advance-shipping-notice": {
        kicker: "Know what is arriving before it does.",
        name: "Advance Shipping Notice",
        body: "Suppliers publish dispatch details — quantities, batches, packing and ETA — so receiving docks plan ahead and receipts post against a known shipment.",
        bullets: ["Supplier-published dispatch data", "Batch, serial and packing detail", "ETA changes pushed to planners", "Pre-matched to PO lines"],
        panelTitle: "Inbound shipments", panelMeta: "next 7 days",
        rows: [
          { label: "On schedule", value: "212 shipments", pill: "OK", warm: false },
          { label: "Delayed", value: "18 shipments", pill: "Alert", warm: true },
          { label: "Partial dispatch", value: "24 shipments", pill: "Review", warm: true },
          { label: "Arriving today", value: "37 shipments", pill: "—", warm: false },
        ],
        statA: "91%", statALabel: "Shipments with ASN", statB: "2.4 days", statBLabel: "Advance visibility",
      },
      "goods-receipt-note": {
        kicker: "Receipts captured where the goods land.",
        name: "Goods Receipt Note",
        body: "Warehouse and site teams confirm quantities, log quality outcomes and attach photos from mobile — feeding matching and supplier scorecards instantly.",
        bullets: ["Mobile and barcode receipting", "Partial and over-delivery rules", "Quality inspection outcomes", "Automatic three-way match trigger"],
        panelTitle: "Receipts today", panelMeta: "128 lines",
        rows: [
          { label: "Full receipt", value: "104 lines", pill: "Matched", warm: false },
          { label: "Partial", value: "16 lines", pill: "Open", warm: true },
          { label: "Quality hold", value: "5 lines", pill: "Hold", warm: true },
          { label: "Rejected", value: "3 lines", pill: "Return", warm: true },
        ],
        statA: "98%", statALabel: "Receipts same-day", statB: "2.1%", statBLabel: "Quality hold rate",
      },
      invoice: {
        kicker: "Touchless from arrival to payment file.",
        name: "Invoice",
        body: "Invoices arrive by email, portal or EDI, are read by the AI engine, matched against PO and receipt, and posted without a human unless something is off.",
        bullets: ["AI extraction with confidence scoring", "Two- and three-way match tolerances", "Duplicate and fraud detection", "Early payment discount capture"],
        panelTitle: "Invoice run", panelMeta: "today",
        rows: [
          { label: "Received", value: "1,382 invoices", pill: "—", warm: false },
          { label: "Auto-matched", value: "1,204 invoices", pill: "88%", warm: false },
          { label: "Exceptions", value: "178 invoices", pill: "Queue", warm: true },
          { label: "Discount captured", value: "€41,200", pill: "Won", warm: false },
        ],
        statA: "88%", statALabel: "Straight-through rate", statB: "$0.94", statBLabel: "Cost per invoice",
      },
      "contract-management": {
        kicker: "A repository that actually knows what it holds.",
        name: "Contract Management",
        body: "Clause libraries, guided authoring, e-signature and obligation tracking — with AI surfacing risk language, renewal dates and price escalators.",
        bullets: ["Clause library with approval routing", "AI clause extraction and risk flags", "Obligation and milestone tracking", "Renewal and escalation alerts"],
        panelTitle: "Contract repository", panelMeta: "3,410 active",
        rows: [
          { label: "Expiring in 30 days", value: "12 contracts", pill: "Act", warm: true },
          { label: "Auto-renew clauses", value: "184 contracts", pill: "Watch", warm: true },
          { label: "Non-standard terms", value: "27 contracts", pill: "Legal", warm: true },
          { label: "Obligations due", value: "58 items", pill: "Owner", warm: false },
        ],
        statA: "$310k", statALabel: "Leakage avoided", statB: "11 days", statBLabel: "Faster to signature",
      },
      "supplier-bulletin-management": {
        kicker: "One channel to your entire supply base.",
        name: "Supplier Bulletin Management",
        body: "Publish policy updates, price requests, shutdown calendars and urgent notices to targeted supplier segments — with read receipts and acknowledgement tracking.",
        bullets: ["Segmented supplier distribution lists", "Acknowledgement and read tracking", "Scheduled and recurring bulletins", "Portal, email and API delivery"],
        panelTitle: "Bulletin BUL-118", panelMeta: "sent Tuesday",
        rows: [
          { label: "Delivered", value: "2,140 suppliers", pill: "Done", warm: false },
          { label: "Opened", value: "1,806 suppliers", pill: "84%", warm: false },
          { label: "Acknowledged", value: "1,522 suppliers", pill: "71%", warm: false },
          { label: "No response", value: "334 suppliers", pill: "Chase", warm: true },
        ],
        statA: "84%", statALabel: "Open rate", statB: "0", statBLabel: "Mailing lists to maintain",
      },
      "supplier-compliance-management": {
        kicker: "Compliance that watches, not just records.",
        name: "Supplier Compliance Management",
        body: "Certificates, policies, sanctions lists and ESG attestations monitored continuously, with automatic escalation the moment something lapses.",
        bullets: ["Continuous sanctions and PEP screening", "Certificate expiry automation", "Policy acknowledgement tracking", "Audit-ready evidence pack export"],
        panelTitle: "Compliance monitor", panelMeta: "live",
        rows: [
          { label: "ISO 9001 certificates", value: "1,204 valid", pill: "OK", warm: false },
          { label: "Insurance policies", value: "18 expiring", pill: "Alert", warm: true },
          { label: "Sanctions screening", value: "12,480 scanned", pill: "Clear", warm: false },
          { label: "Code of conduct", value: "97% signed", pill: "OK", warm: false },
        ],
        statA: "24h", statALabel: "Lapse detection", statB: "100%", statBLabel: "Evidence retained",
      },
      "supplier-performance-management": {
        kicker: "Scorecards that build themselves.",
        name: "Supplier Performance Management",
        body: "Delivery, quality, responsiveness and compliance pulled straight from transactions — weighted your way, reviewed on your cadence.",
        bullets: ["Automated KPI capture from transactions", "Weighted category scorecards", "Improvement plans with owners", "Quarterly business review packs"],
        panelTitle: "Scorecard Q3", panelMeta: "top suppliers",
        rows: [
          { label: "On-time delivery", value: "94.2%", pill: "▲", warm: false },
          { label: "Quality acceptance", value: "98.7%", pill: "▲", warm: false },
          { label: "Responsiveness", value: "88.0%", pill: "▼", warm: false },
          { label: "Compliance", value: "100%", pill: "=", warm: false },
        ],
        statA: "94.2%", statALabel: "On-time delivery", statB: "+7 pts", statBLabel: "Year on year",
      },
      "supply-chain-issue-management": {
        kicker: "Disruptions tracked to closure, not to inbox.",
        name: "Supply Chain Issue Management",
        body: "Shortages, quality defects, delays and logistics failures raised as structured cases with owners, severity, root cause and corrective actions — visible to both sides.",
        bullets: ["Structured case intake and triage", "Severity-based SLAs and escalation", "8D and root-cause workflows", "Shared supplier resolution view"],
        panelTitle: "Open issues", panelMeta: "live",
        rows: [
          { label: "Critical", value: "3 cases", pill: "Alert", warm: true },
          { label: "Delivery delay", value: "21 cases", pill: "Open", warm: true },
          { label: "Quality defect", value: "14 cases", pill: "Review", warm: true },
          { label: "Closed this month", value: "96 cases", pill: "Done", warm: false },
        ],
        statA: "3.2 days", statALabel: "Median to resolution", statB: "68%", statBLabel: "Recurrence prevented",
      },
      "supplier-certification": {
        kicker: "Certificates that stay current on their own.",
        name: "Supplier Certification",
        body: "Collect, validate and re-verify quality, safety, insurance and ESG certifications with expiry automation — so an out-of-date document never reaches an award decision.",
        bullets: ["Certificate collection and validation", "Automated expiry and renewal chasing", "Category-specific requirement sets", "Blocking rules on award and PO"],
        panelTitle: "Certification register", panelMeta: "12,480 suppliers",
        rows: [
          { label: "Valid", value: "11,402 suppliers", pill: "OK", warm: false },
          { label: "Expiring 30 days", value: "612 suppliers", pill: "Chase", warm: true },
          { label: "Expired", value: "184 suppliers", pill: "Alert", warm: true },
          { label: "Pending validation", value: "282 suppliers", pill: "Review", warm: true },
        ],
        statA: "91%", statALabel: "Certificates current", statB: "0", statBLabel: "Manual reminder emails",
      },
      "supplier-rating": {
        kicker: "One number your category managers trust.",
        name: "Supplier Rating",
        body: "Weighted ratings combine performance, compliance, risk and commercial data into a single comparable score — published to suppliers and used in award decisions.",
        bullets: ["Configurable weighting per category", "Rating history and trend lines", "Supplier-visible rating breakdown", "Rating thresholds in sourcing rules"],
        panelTitle: "Category ratings", panelMeta: "Q3",
        rows: [
          { label: "Kernwerk GmbH", value: "A · 92", pill: "▲", warm: false },
          { label: "Voltara Energy", value: "A · 89", pill: "=", warm: false },
          { label: "Sumira Polymers", value: "B · 74", pill: "▼", warm: false },
          { label: "Brightpath Labs", value: "C · 61", pill: "Review", warm: true },
        ],
        statA: "16", statALabel: "Suppliers upgraded", statB: "4", statBLabel: "Suppliers phased out",
      },
    },
    analytics: {
      kicker: "Analytics & dashboards",
      title: "Every stage instrumented by default",
      lede: "Cycle time, savings realisation, compliance and supplier risk update as transactions move. Slice by entity, category, buyer or supplier — then schedule it to the people who need it.",
    },
    analyticsCards: [
      { title: "Cycle time analytics", body: "Stage-by-stage duration with bottleneck detection by team, category and entity." },
      { title: "Savings realisation", body: "Identified, negotiated and realised savings tracked to the general ledger." },
      { title: "Compliance dashboard", body: "On-contract buying, policy breaches and approval exceptions in one view." },
      { title: "Supplier risk view", body: "Concentration, geography, financial health and certificate status combined." },
      { title: "Working capital", body: "Payment terms, discount capture and DPO modelling by supplier segment." },
      { title: "Scheduled reporting", body: "Board-ready packs delivered to inboxes on your reporting calendar." },
    ],
    enterpriseFeaturesTitle: "Enterprise features",
    enterpriseFeatures: [
      { title: "Multi-entity and multi-currency", body: "Separate legal entities, tax logic and reporting currencies in one tenant." },
      { title: "Configurable approval matrix", body: "Thresholds, delegation and segregation of duties without code changes." },
      { title: "Localisation", body: "Interface, documents and supplier portal in your operating languages." },
      { title: "Data residency options", body: "EU, US, UK, UAE and India hosting with no cross-region processing." },
      { title: "Sandbox and release control", body: "Test configuration changes, then promote with a full change record." },
      { title: "Enterprise support", body: "Named architect, 24/7 coverage and a 99.95% uptime commitment." },
    ],
    connectedTitle: "Connected to your stack",
    connectedLede: "Bidirectional sync with ERP masters, GL, budgets and payment runs. Certified connectors, or build your own on the open API.",
    integrations: ["SAP S/4HANA", "Oracle Fusion", "NetSuite", "Dynamics 365", "Workday", "Coupa", "DocuSign", "Snowflake", "Power BI", "Azure AD", "EDI / cXML", "REST & Webhooks"],
    seeApis: "See platform APIs",
    cta: {
      title: "Start with one module. Keep the rest waiting",
      body: "Most teams begin with invoices or supplier onboarding and expand from there. We will show you the shortest path to a measurable win.",
      primaryLabel: "Book a walkthrough",
    },
  },

  contact: {
    breadcrumb: "Contact Us",
    hero: {
      title: "Let's Transform Your",
      titleLine2: "Procurement Strategy",
      lede: "Want to see how Mercoris could fit your business needs, or explore a quick demo? Talk to us.",
    },
    intents: [
      { title: "Find the right fit", body: "Discuss the best Mercoris solution to meet your business goals, workflows and procurement priorities." },
      { title: "See the full cycle", body: "Discover how all our Source-to-Pay apps work together in harmony to deliver a complete sourcing cycle." },
      { title: "Get tailored pricing", body: "Pricing based on your business size and the Source-to-Pay capabilities you actually need." },
    ],
    getInTouch: "Get in touch",
    phoneLabel: "Phone",
    emailLabel: "Email address",
    emailNote: "Every enquiry is read by a specialist — we reply within one business day.",
    offices: {
      US: { country: "United States" },
      DE: { country: "Germany" },
      IN: { country: "India" },
    },
    faqTitle: "Frequently asked",
    faqs: [
      { q: "How long does a Mercoris implementation take?", a: "A first module in production typically takes six weeks with your data and your approval rules. Full Source-to-Pay across multiple entities is usually a phased programme of six to nine months, with value delivered at each phase rather than at the end." },
      { q: "Can Mercoris work alongside our existing ERP?", a: "Yes. Mercoris is designed to sit in front of ERP, not replace it. Certified connectors handle supplier masters, cost centres, budgets, GL codes and payment runs bidirectionally, and the open REST API covers anything a connector does not." },
      { q: "Do you offer a pilot?", a: "We run paid proof-of-value engagements on one category or one process, using your real data in an isolated tenant. If it does not hit the agreed measures, you walk away with the analysis and no platform commitment." },
      { q: "How is our data protected?", a: "ISO 27001 and SOC 2 Type II certified controls, AES-256 encryption at rest, TLS 1.3 in transit, regional data residency and optional customer-managed keys. Your data is never used to train models." },
      { q: "What does pricing look like?", a: "Platform subscription based on modules deployed and transaction volume, with unlimited internal users and no charge for supplier accounts. We will give you an indicative range on the first call." },
      { q: "Who will we actually work with?", a: "A named solution architect from discovery through go-live, supported by a delivery team that stays with you into hypercare. Enterprise plans include 24/7 support and a quarterly service review." },
    ],
    form: {
      nameLabel: "Full Name",
      namePlaceholder: "Enter your full name",
      companyLabel: "Company",
      companyHint: "(optional)",
      companyPlaceholder: "Enter your company name",
      emailLabel: "Email Address",
      emailPlaceholder: "Enter your email",
      phoneLabel: "Phone Number",
      phonePlaceholder: "Enter your phone number",
      messageLabel: "How can we help you?",
      messagePlaceholder: "Tell us about your requirement",
      submit: "Submit",
      submitting: "Sending…",
      errors: {
        name: "Please tell us your name.",
        emailRequired: "Please add a work email.",
        emailInvalid: "That email address does not look right.",
        phoneRequired: "Please add a phone number.",
        phoneInvalid: "Use digits, spaces and an optional +.",
        message: "A sentence or two helps us route your enquiry.",
      },
      statusInvalid: "Please check the highlighted fields and try again.",
      statusSuccess: "Thank you — your message is with us. A procurement specialist will reply within one business day.",
      statusError: "We could not send that just now. Please retry, or email contact@sigvion.com.",
    },
  },

  notFound: {
    kicker: "404",
    title: "That page has moved or never existed",
    lede: "Try the Source-to-Pay suite, the platform tour, or tell us what you were looking for.",
    backHome: "Back to home",
    contactUs: "Contact us",
  },
};

export default en;
